import React, { useState, useMemo, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import './FidelityPopup.css';
import {
  RANGE_OPTIONS,
  periodKpiLabel,
  computePerf,
  hasEnoughHistory,
  filterHistoryToRange,
  computeYDomain,
} from './popupShared';

// Map range label → per-position P&L field for the "Return" column.
// 1W uses WTD snapshot; others accumulate over time.
const RANGE_PNL_FIELD = {
  '1D':  null,
  '1W':  'wtdPnl',
  '1M':  null,
  '3M':  null,
  '6M':  null,
  '1Y':  null,
  'YTD': null,
};

// ─── Formatters ───────────────────────────────────────────────────────────────

function fmtEur(value, showSign = false) {
  if (value == null || isNaN(value)) return '—';
  const sign = showSign ? (value >= 0 ? '+' : '') : '';
  return `${sign}€${Math.round(Math.abs(value)).toLocaleString()}`;
}

function fmtPct(value) {
  if (value == null || isNaN(value)) return '';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function fmtNum(value, decimals = 2) {
  if (value == null || isNaN(value)) return '—';
  return value.toLocaleString(undefined, { maximumFractionDigits: decimals });
}

function pnlClass(value) {
  if (value == null || isNaN(value)) return '';
  return value >= 0 ? 'fid-pos' : 'fid-neg';
}

function formatYAxis(value) {
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000)     return `€${(value / 1_000).toFixed(0)}k`;
  return `€${value}`;
}

function formatXAxis(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// ─── Chart tooltip ────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const dateLabel = new Date(label).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
  return (
    <div className="fid-chart-tooltip">
      <div className="fid-chart-tooltip-date">{dateLabel}</div>
      <div>€{Math.round(payload[0]?.value ?? 0).toLocaleString()}</div>
    </div>
  );
}

// ─── Sort helpers ─────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'description',      label: 'Instrument',      align: 'left'  },
  { key: 'assetType',        label: 'Type',            align: 'left'  },
  { key: 'amount',           label: 'Qty',             align: 'right' },
  { key: 'currentPrice',     label: 'Price',           align: 'right' },
  { key: 'localExposure',    label: 'Local Exposure',  align: 'right' },
  { key: 'exposureInBase',   label: 'Exposure (EUR)',  align: 'right' },
  { key: 'pnlOnTradeInBase', label: 'P&L',             align: 'right' },
  { key: '__return__',       label: 'Return',          align: 'right' },
];

function getReturnValue(pos, rangeLabel) {
  const field = RANGE_PNL_FIELD[rangeLabel];
  if (!field) return null;
  return pos[field] ?? null;
}

// Group ordering: equities/ETFs/options/futures (0), bonds (1), cash (2), adjustments (3).
// Used as the primary sort key so the breakdown always reads stocks → bonds → cash → adjustments,
// regardless of which column the user is sorting by.
function categoryRank(assetType) {
  const t = (assetType || '').toUpperCase();
  if (t === 'ADJUSTMENT') return 3;
  if (t === 'CASH')       return 2;
  if (t === 'BOND' || t === 'GOVT' || t === 'NOTE' || t === 'FIXED INCOME') return 1;
  return 0;
}

function sortPositions(positions, sortKey, sortDir, rangeLabel) {
  return [...positions].sort((a, b) => {
    // Primary: category (always ascending: stocks → bonds → cash → adjustments)
    const ra = categoryRank(a.assetType);
    const rb = categoryRank(b.assetType);
    if (ra !== rb) return ra - rb;

    // Secondary: user-selected column
    let va = sortKey === '__return__' ? getReturnValue(a, rangeLabel) : a[sortKey];
    let vb = sortKey === '__return__' ? getReturnValue(b, rangeLabel) : b[sortKey];

    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;

    if (typeof va === 'string') va = va.toLowerCase();
    if (typeof vb === 'string') vb = vb.toLowerCase();

    if (va < vb) return sortDir === 'asc' ? -1 :  1;
    if (va > vb) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });
}

// ─── Main component ───────────────────────────────────────────────────────────

function FidelityPopup({ onClose, data, loading, error }) {
  const [range, setRange]     = useState('1W');
  const [sortKey, setSortKey] = useState('pnlOnTradeInBase');
  const [sortDir, setSortDir] = useState('desc');

  // ── Admin: ISIN/symbol management ──
  const [adminMode, setAdminMode]            = useState(false);
  const [adminPositions, setAdminPositions]  = useState([]);
  const [adminLoading, setAdminLoading]      = useState(false);
  const [draftSymbol, setDraftSymbol]        = useState({});  // positionId → string
  const [suggestions, setSuggestions]        = useState({});  // positionId → array
  const [feedback, setFeedback]              = useState({});  // positionId → { kind, msg }
  const [busyByPos, setBusyByPos]            = useState({});  // positionId → boolean

  const loadAdminPositions = async () => {
    setAdminLoading(true);
    try {
      const res = await apiGet('/api/fidelity/positions/admin');
      if (res.success) setAdminPositions(res.positions);
    } finally { setAdminLoading(false); }
  };

  // Load admin data when the panel opens for the first time
  useEffect(() => {
    if (adminMode && !adminPositions.length && !adminLoading) loadAdminPositions();
  }, [adminMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const setBusy   = (id, v) => setBusyByPos(b => ({ ...b, [id]: v }));
  const setMsg    = (id, kind, msg) => setFeedback(f => ({ ...f, [id]: { kind, msg } }));
  const setDraft  = (id, v) => setDraftSymbol(d => ({ ...d, [id]: v }));

  const fetchSuggestions = async (pos) => {
    setBusy(pos.positionId, true);
    setMsg(pos.positionId, 'neutral', 'Searching FT…');
    try {
      const res = await apiGet(`/api/fidelity/search?q=${encodeURIComponent(pos.description)}`);
      if (res.success && res.results.length) {
        // Verify each candidate's price → score by drift to expected GBP
        const scored = [];
        for (const r of res.results) {
          try {
            const v = await apiPost('/api/fidelity/verify', { symbol: r.symbol, quantity: pos.quantity });
            if (v.success && v.valueGbp) {
              const expected = pos.expectedGbp || 1;
              const drift = Math.abs(v.valueGbp - expected) / expected;
              const driftPence = Math.abs((v.valueGbp / 100) - expected) / expected;
              scored.push({ ...r, livePrice: v.price, livePriceUnit: v.priceUnit, valueGbp: v.valueGbp, drift: Math.min(drift, driftPence) });
            } else {
              scored.push({ ...r, drift: 9 });
            }
          } catch { scored.push({ ...r, drift: 9 }); }
        }
        scored.sort((a, b) => a.drift - b.drift);
        setSuggestions(s => ({ ...s, [pos.positionId]: scored }));
        setMsg(pos.positionId, 'neutral', `${scored.length} suggestions — green = best price match`);
      } else {
        setSuggestions(s => ({ ...s, [pos.positionId]: [] }));
        setMsg(pos.positionId, 'bad', 'No FT candidates found');
      }
    } catch (e) {
      setMsg(pos.positionId, 'bad', e.message);
    } finally { setBusy(pos.positionId, false); }
  };

  const verifySymbol = async (pos) => {
    const sym = (draftSymbol[pos.positionId] || pos.ftSymbol || '').trim();
    if (!sym) { setMsg(pos.positionId, 'bad', 'Enter an ISIN or symbol first'); return; }
    setBusy(pos.positionId, true);
    setMsg(pos.positionId, 'neutral', 'Fetching live price…');
    try {
      const ftSym = sym.includes(':') ? sym : `${sym}:GBP`;
      const v = await apiPost('/api/fidelity/verify', { symbol: ftSym, quantity: pos.quantity });
      if (v.success && v.valueGbp) {
        const expected = pos.expectedGbp || 1;
        const driftP   = Math.abs(v.valueGbp - expected) / expected;
        const driftp   = Math.abs((v.valueGbp / 100) - expected) / expected;
        const drift    = Math.min(driftP, driftp);
        const ok       = drift < 0.10;
        setMsg(pos.positionId, ok ? 'good' : 'bad',
          `Live £${v.price} → £${v.valueGbp.toFixed(2)} (expected £${expected.toFixed(2)}, drift ${(drift*100).toFixed(1)}%)`);
      } else {
        setMsg(pos.positionId, 'bad', v.message || 'No price for that symbol');
      }
    } catch (e) {
      setMsg(pos.positionId, 'bad', e.message);
    } finally { setBusy(pos.positionId, false); }
  };

  const saveSymbol = async (pos, symbolOverride) => {
    const sym = (symbolOverride || draftSymbol[pos.positionId] || pos.ftSymbol || '').trim();
    if (!sym) { setMsg(pos.positionId, 'bad', 'Enter an ISIN or symbol first'); return; }
    setBusy(pos.positionId, true);
    try {
      const ftSym = sym.includes(':') ? sym : `${sym}:GBP`;
      const res = await apiPut(`/api/fidelity/positions/${encodeURIComponent(pos.positionId)}/symbol`, { ftSymbol: ftSym });
      if (res.success) {
        setMsg(pos.positionId, 'good', `Saved — price refreshed to £${res.position.currentPrice}`);
        setDraft(pos.positionId, '');
        await loadAdminPositions();
      } else {
        setMsg(pos.positionId, 'bad', res.message || 'Save failed');
      }
    } catch (e) {
      setMsg(pos.positionId, 'bad', e.message);
    } finally { setBusy(pos.positionId, false); }
  };

  const clearSymbol = async (pos) => {
    setBusy(pos.positionId, true);
    try {
      await apiDelete(`/api/fidelity/positions/${encodeURIComponent(pos.positionId)}/symbol`);
      await loadAdminPositions();
      setMsg(pos.positionId, 'neutral', 'Cleared');
    } catch (e) {
      setMsg(pos.positionId, 'bad', e.message);
    } finally { setBusy(pos.positionId, false); }
  };

  const adminCounts = useMemo(() => {
    const resolved = adminPositions.filter(p => p.resolved).length;
    return { resolved, stale: adminPositions.length - resolved, total: adminPositions.length };
  }, [adminPositions]);

  const valueHistory = useMemo(
    () => data?.account?.valueHistory || [],
    [data]
  );
  const selectedOption = useMemo(
    () => RANGE_OPTIONS.find(r => r.label === range) || RANGE_OPTIONS[1],
    [range]
  );

  const chartData = useMemo(
    () => filterHistoryToRange(valueHistory, selectedOption),
    [valueHistory, selectedOption]
  );
  const yDomain = useMemo(() => computeYDomain(chartData), [chartData]);

  const periodPerf = useMemo(
    () => computePerf(valueHistory, selectedOption),
    [valueHistory, selectedOption]
  );

  // Sorted positions
  const positions = useMemo(() => {
    const raw = data?.positions || [];
    return sortPositions(raw, sortKey, sortDir, range);
  }, [data, sortKey, sortDir, range]);

  const account = data?.account || {};

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortIcon = (key) => {
    if (key !== sortKey) return <span className="fid-sort-icon">⇅</span>;
    return <span className="fid-sort-icon">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  // Totals row
  const totals = useMemo(() => {
    if (!positions.length) return null;
    const sumExp    = positions.reduce((s, p) => s + (p.exposureInBase  ?? 0), 0);
    const sumPnl    = positions.reduce((s, p) => s + (p.pnlOnTradeInBase ?? 0), 0);
    const returnPnls = positions.map(p => getReturnValue(p, range)).filter(v => v != null);
    const sumReturn  = returnPnls.length ? returnPnls.reduce((s, v) => s + v, 0) : null;
    return { sumExp, sumPnl, sumReturn };
  }, [positions, range]);

  // Format asOf date for footnote
  const asOfLabel = account.asOf
    ? new Date(account.asOf).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  // ─── Render states ──────────────────────────────────────────────────────────
  const renderBody = () => {
    if (loading) return <div className="fid-loading">Loading portfolio…</div>;
    if (error)   return (
      <div className="fid-error"><p>⚠ {error}</p><button onClick={onClose}>Close</button></div>
    );

    const returnLabel = RANGE_PNL_FIELD[range] ? `Return (${range})` : `Return (${range}) ¹`;

    return (
      <>
        {/* ── Account summary bar ── */}
        <div className="fid-account-bar">
          <div className="fid-account-total">
            <span className="fid-label">Portfolio Value</span>
            <span className="fid-big-value">
              €{Math.round(account.totalEurValue || 0).toLocaleString()}
            </span>
          </div>
          <div className="fid-account-stat">
            <span className="fid-label">Today</span>
            <span className={`ib-pnl ${pnlClass(account.dailyPnl)}`}>
              {fmtEur(account.dailyPnl, true)}
              {account.dailyPnlPercent != null && (
                <span className="fid-pct"> ({fmtPct(account.dailyPnlPercent)})</span>
              )}
            </span>
          </div>
          <div className="fid-account-stat">
            <span className="fid-label">{periodKpiLabel(selectedOption)}</span>
            <span className={`ib-pnl ${pnlClass(periodPerf?.abs)}`}>
              {periodPerf ? fmtEur(periodPerf.abs, true) : '—'}
              {periodPerf?.pct != null && (
                <span className="fid-pct"> ({fmtPct(periodPerf.pct)})</span>
              )}
            </span>
          </div>
        </div>

        {/* ── Performance chart ── */}
        <div className="fid-chart-section">
          <div className="fid-chart-header">
            <span className="fid-section-title">Account Performance</span>
            <div className="fid-range-buttons">
              {RANGE_OPTIONS.map(r => {
                const enabled = hasEnoughHistory(valueHistory, r);
                return (
                  <button
                    key={r.label}
                    className={`ib-range-btn ${range === r.label ? 'active' : ''}`}
                    onClick={() => enabled && setRange(r.label)}
                    disabled={!enabled}
                    title={enabled ? '' : 'Not enough history for this range yet'}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>
          {chartData.length > 1 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={formatXAxis} tick={{ fontSize: 11 }} minTickGap={40} />
                <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11 }} width={72} domain={yDomain} allowDataOverflow={false} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="eurValue" stroke="#EF4444" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="fid-chart-empty">Not enough history for this range yet.</div>
          )}
        </div>

        {/* ── Positions table ── */}
        <div className="fid-positions-section">
          <div className="fid-section-title">Positions ({positions.length})</div>
          {positions.length === 0 ? (
            <div className="fid-chart-empty">No open positions found.</div>
          ) : (
            <div className="fid-table-wrapper">
              <table className="fid-table">
                <thead>
                  <tr>
                    {COLUMNS.map(col => (
                      <th
                        key={col.key}
                        className={`${col.align === 'right' ? 'fid-num' : ''} ib-th-sortable`}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.key === '__return__' ? returnLabel : col.label}
                        {sortIcon(col.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {positions.map((pos, i) => {
                    const returnVal = getReturnValue(pos, range);
                    return (
                      <tr key={pos.positionId || i}>
                        {/* Instrument */}
                        <td className="fid-desc" title={pos.description}>{pos.description || '—'}</td>

                        {/* Type */}
                        <td><span className="fid-type-badge">{pos.assetType || '—'}</span></td>

                        {/* Qty */}
                        <td className="fid-num">{fmtNum(pos.amount, 0)}</td>

                        {/* Price */}
                        <td className="fid-num">
                          {pos.localCurrency} {fmtNum(pos.currentPrice, 2)}
                        </td>

                        {/* Local Exposure */}
                        <td className="fid-num">
                          {pos.localCurrency} {fmtNum(pos.localExposure, 0)}
                        </td>

                        {/* Exposure EUR */}
                        <td className="fid-num">
                          €{fmtNum(pos.exposureInBase, 0)}
                        </td>

                        {/* P&L */}
                        <td className={`ib-num ${pnlClass(pos.pnlOnTradeInBase)}`}>
                          <div>{fmtEur(pos.pnlOnTradeInBase, true)}</div>
                          {pos.pnlOnTradePercent != null && (
                            <div className="fid-pct">{fmtPct(pos.pnlOnTradePercent)}</div>
                          )}
                        </td>

                        {/* Return (range-tied) */}
                        <td className={`ib-num ${pnlClass(returnVal)}`}>
                          {returnVal != null ? fmtEur(returnVal, true) : '—'}
                        </td>
                      </tr>
                    );
                  })}

                  {/* ── Totals row ── */}
                  {totals && (
                    <tr className="fid-totals-row">
                      <td colSpan={4}><strong>TOTAL</strong></td>
                      <td className="fid-num">—</td>
                      <td className="fid-num"><strong>€{fmtNum(totals.sumExp, 0)}</strong></td>
                      <td className={`ib-num ${pnlClass(totals.sumPnl)}`}>
                        <strong>{fmtEur(totals.sumPnl, true)}</strong>
                      </td>
                      <td className={`ib-num ${pnlClass(totals.sumReturn)}`}>
                        <strong>{totals.sumReturn != null ? fmtEur(totals.sumReturn, true) : '—'}</strong>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {!RANGE_PNL_FIELD[range] && (
            <p className="fid-footnote">
              ¹ Per-position {range} data builds up from daily snapshots — available after sufficient history is accumulated.
            </p>
          )}
          {asOfLabel && (
            <p className="fid-footnote">
              Prices as of last market close ({asOfLabel}) — Fidelity Flex Web Service
            </p>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content ib-popup-content" onClick={e => e.stopPropagation()}>
        <div className="fid-popup-header">
          <h2>Fidelity ISA — Portfolio</h2>
          <div>
            <button
              className={'fid-admin-toggle' + (adminMode ? ' active' : '')}
              onClick={() => setAdminMode(m => !m)}
              title="Manage ISINs / FT symbols for daily price refresh">
              {adminMode ? 'Done' : 'Manage symbols'}
            </button>
            <button className="fid-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>
        {adminMode && (
          <div className="fid-admin-panel">
            <h3>Manage FT.com symbols</h3>
            <p className="fid-admin-help">
              Enter the ISIN (or full FT symbol like <code>GB00B41YBW71:GBP</code>) for each holding so the
              daily 21:15 cron can refresh its price. Press <strong>Suggest</strong> to fetch FT candidates
              ranked by which one&apos;s live price matches your expected position value.
            </p>
            <div className="fid-admin-counts">
              <span className="fid-resolved">✓ {adminCounts.resolved} resolved</span>
              <span className="fid-stale">⚠ {adminCounts.stale} stale</span>
              <span>{adminCounts.total} total</span>
            </div>
            {adminLoading && <div>Loading…</div>}
            {!adminLoading && adminPositions.map(pos => {
              const sugg = suggestions[pos.positionId] || [];
              const fb   = feedback[pos.positionId];
              const busy = !!busyByPos[pos.positionId];
              return (
                <div key={pos.positionId} className={'fid-admin-row' + (pos.resolved ? '' : ' fid-stale-row')}>
                  <div>
                    <div className="fid-pos-name" title={pos.description}>{pos.description}</div>
                    <div className="fid-pos-meta">qty {fmtNum(pos.quantity, 4)} · expected £{fmtNum(pos.expectedGbp, 0)}</div>
                  </div>
                  <div className="fid-pos-meta">{pos.ftSymbol || <em>(stale)</em>}</div>
                  <input
                    className="fid-isin-input"
                    placeholder="ISIN or symbol"
                    value={draftSymbol[pos.positionId] ?? ''}
                    onChange={e => setDraft(pos.positionId, e.target.value)}
                    disabled={busy}
                  />
                  <div>
                    {sugg.slice(0, 4).map((c, i) => (
                      <span
                        key={c.symbol}
                        className={'fid-suggest' + (i === 0 && c.drift < 0.10 ? ' fid-suggest-best' : '')}
                        title={(c.name || '') + ' · drift ' + (c.drift < 9 ? (c.drift*100).toFixed(1) + '%' : '?')}
                        onClick={() => { setDraft(pos.positionId, c.symbol); }}
                      >{c.symbol}</span>
                    ))}
                    {fb && <div className={'fid-admin-feedback fid-' + fb.kind}>{fb.msg}</div>}
                  </div>
                  <div className="fid-admin-actions">
                    <button onClick={() => fetchSuggestions(pos)} disabled={busy}>Suggest</button>
                    <button onClick={() => verifySymbol(pos)} disabled={busy}>Verify</button>
                    <button onClick={() => saveSymbol(pos)} className="fid-primary" disabled={busy}>Save</button>
                    {pos.ftSymbol && <button onClick={() => clearSymbol(pos)} className="fid-clear" disabled={busy}>Clear</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {renderBody()}
      </div>
    </div>
  );
}

export default FidelityPopup;
